// ============================================
// Shared Mobile App Header — Driver / PA Role
// Avatar tap → Settings & Logout dropdown
// Mobile only (sm:hidden)
// ============================================

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logoutUser } from '../../redux/slices/authSlice';
import NotificationBell from '../common/NotificationBell';

const DriverMobileHeader = ({ user, theme, toggleTheme, settingsPath = '/driver/settings' }) => {
    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening';
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        if (menuOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [menuOpen]);

    const handleLogout = () => {
        setMenuOpen(false);
        dispatch(logoutUser());
    };

    const handleSettings = () => {
        setMenuOpen(false);
        navigate(settingsPath);
    };

    const isDark = theme === 'dark';

    return (
        <header
            className={`sm:hidden flex-shrink-0 z-50 relative ${isDark ? 'bg-slate-900/80 backdrop-blur-3xl border-b border-white/5' : 'bg-white/80 backdrop-blur-3xl border-b border-slate-200/50'}`}
            style={{
                paddingTop: 'max(env(safe-area-inset-top, 0px), 16px)',
                paddingBottom: '16px',
                paddingLeft: '20px',
                paddingRight: '20px',
            }}
        >
            {/* Row: Avatar + Greeting + Actions */}
            <div className="flex items-center justify-between">
                {/* Left: Avatar (tappable) + greeting */}
                <div className="flex items-center gap-3" ref={menuRef}>
                    {/* Avatar button */}
                    <button
                        onClick={() => setMenuOpen(v => !v)}
                        className="relative flex-shrink-0 focus:outline-none active:scale-90 transition-transform"
                        aria-label="Profile menu"
                    >
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-black text-sm shadow-lg shadow-teal-500/30 ring-2 ring-teal-400/30 select-none">
                            {user?.name?.charAt(0)?.toUpperCase() || 'D'}
                        </div>
                        {/* Small chevron indicator */}
                        <span className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center shadow-sm border transition-all duration-200 ${isDark ? 'bg-slate-800 border-white/10' : 'bg-white border-slate-200'}`}>
                            <svg className={`w-2.5 h-2.5 transition-transform duration-200 ${isDark ? 'text-slate-300' : 'text-slate-500'} ${menuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                            </svg>
                        </span>
                    </button>

                    {/* Greeting */}
                    <div>
                        <p className={`text-[10px] font-semibold uppercase tracking-widest leading-none ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                            {greeting}
                        </p>
                        <p className={`text-[15px] font-black mt-0.5 leading-tight tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {user?.name?.split(' ')[0] || 'Driver'}
                        </p>
                    </div>

                    {/* Dropdown Menu */}
                    {menuOpen && (
                        <div className={`absolute top-full left-4 mt-2 w-56 rounded-2xl shadow-2xl border z-[200] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 ${isDark ? 'bg-slate-900/95 backdrop-blur-3xl border-white/10' : 'bg-white/95 backdrop-blur-3xl border-slate-200/60'}`}>
                            {/* User info header */}
                            <div className={`px-4 py-3 border-b ${isDark ? 'border-white/5' : 'border-slate-100'}`}>
                                <p className={`text-xs font-black truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{user?.name || 'Driver'}</p>
                                <p className={`text-[10px] font-medium truncate mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{user?.email || ''}</p>
                            </div>

                            {/* Settings */}
                            <button
                                onClick={handleSettings}
                                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors active:scale-95 ${isDark ? 'text-slate-300 hover:bg-white/5' : 'text-slate-700 hover:bg-slate-50'}`}
                            >
                                <span className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                                    </svg>
                                </span>
                                <span className="text-xs font-black uppercase tracking-widest">Settings</span>
                            </button>

                            {/* Divider */}
                            <div className={`mx-4 h-px ${isDark ? 'bg-white/5' : 'bg-slate-100'}`} />

                            {/* Logout */}
                            <button
                                onClick={handleLogout}
                                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors active:scale-95 ${isDark ? 'text-rose-400 hover:bg-rose-500/5' : 'text-rose-600 hover:bg-rose-50'}`}
                            >
                                <span className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-rose-500/10' : 'bg-rose-50'}`}>
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                    </svg>
                                </span>
                                <span className="text-xs font-black uppercase tracking-widest">Sign Out</span>
                            </button>
                        </div>
                    )}
                </div>

                {/* Right: theme toggle + notifications */}
                <div className="flex items-center gap-2">
                    <button
                        onClick={toggleTheme}
                        className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all active:scale-90 shadow-sm ${isDark ? 'bg-white/10 border-white/10 text-slate-300 hover:bg-white/15' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                        aria-label="Toggle theme"
                    >
                        {isDark
                            ? <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                            : <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
                        }
                    </button>
                    <NotificationBell />
                </div>
            </div>
        </header>
    );
};

export default DriverMobileHeader;
